import type { IntentCluster, PerQueryEntry, RivalGap } from '@/lib/types'

// LeaderEntry is one brand's standing in a space: how many answers named it,
// flagged when it's the target brand (so the UI can highlight it in the list).
export interface LeaderEntry {
  name: string
  count: number
  isBrand: boolean
}

// spaceLeaderboard ranks, within one space, the target brand AND every rival by
// how many answers named each — the brand counted whenever it was mentioned
// (mention !== 'none'), rivals counted whenever they appear in an answer's
// competitors. Sorted desc, brand included so the UI can show where it falls.
export function spaceLeaderboard(
  clusterId: string,
  perQuery: PerQueryEntry[],
  brand: string
): LeaderEntry[] {
  let brandCount = 0
  const rivals = new Map<string, number>()

  for (const q of perQuery) {
    if (q.clusterId !== clusterId) continue

    for (const resp of q.responses) {
      if (resp.error) continue

      if (resp.mention !== 'none') brandCount++

      for (const name of resp.competitors) {
        if (name && name.toLowerCase() !== brand.toLowerCase()) {
          rivals.set(name, (rivals.get(name) ?? 0) + 1)
        }
      }
    }
  }

  const out: LeaderEntry[] = [{ name: brand, count: brandCount, isBrand: true }]

  for (const [name, count] of rivals) {
    out.push({ name, count, isBrand: false })
  }

  // Rank by mentions desc; ties keep the brand ahead, then alphabetical.
  return out.sort(
    (a, b) =>
      b.count - a.count ||
      Number(b.isBrand) - Number(a.isBrand) ||
      a.name.localeCompare(b.name)
  )
}

// brandMentionsInSpace counts how many answers in a space named the brand — the
// "you got N mentions here" figure shown on each Spaces bar.
export function brandMentionsInSpace(
  clusterId: string,
  perQuery: PerQueryEntry[]
): { brand: number; total: number } {
  let brand = 0
  let total = 0

  for (const q of perQuery) {
    if (q.clusterId !== clusterId) continue

    for (const resp of q.responses) {
      if (resp.error) continue
      total++
      if (resp.mention !== 'none') brand++
    }
  }

  return { brand, total }
}

// rivalGaps derives the Rivals lens from the per-query data: for each space, the
// competitors named in answers where the brand was ABSENT (mention "none"),
// ranked by frequency. Mirrors the backend's rivalGaps so the UI needn't wait on
// a persisted field. Spaces with no gap rivals are omitted.
export function rivalGaps(
  clusters: IntentCluster[],
  perQuery: PerQueryEntry[]
): RivalGap[] {
  const labelById = new Map(clusters.map((c) => [c.id, c.label]))

  // clusterId -> competitor name -> count (from none-mention answers only)
  const byCluster = new Map<string, Map<string, number>>()
  const order: string[] = []

  for (const q of perQuery) {
    const cid = q.clusterId
    if (!cid) continue

    for (const resp of q.responses) {
      if (resp.error || resp.mention !== 'none') continue

      let counts = byCluster.get(cid)
      if (!counts) {
        counts = new Map()
        byCluster.set(cid, counts)
        order.push(cid)
      }

      for (const name of resp.competitors) {
        if (name) counts.set(name, (counts.get(name) ?? 0) + 1)
      }
    }
  }

  const out: RivalGap[] = []

  for (const cid of order) {
    const counts = byCluster.get(cid)
    if (!counts || counts.size === 0) continue

    const rivals = [...counts.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))

    out.push({
      clusterId: cid,
      clusterLabel: labelById.get(cid) ?? cid,
      rivals,
    })
  }

  return out
}
